import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { useMutation } from "@apollo/react-hooks";
import { Box } from "@mui/material";
import {
  orphanedLeaveRequests,
  orphanedLeaveRequestsIsMobile,
} from "my-leave/configs/table-defs";
import {
  MY_LEAVE_APPROVE_REQUEST,
  MY_LEAVE_REJECT_REQUEST,
} from "my-leave/graphql";
import { useMyLeaveOrphanedRequestsList } from "my-leave/hooks";
import {
  OcCard,
  OcDataTable,
  OcPageNameTitle,
  OcPageTransition,
} from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import { useOcTableState, useOcIsMobile, useOcSnackbar } from "oc/hooks";
import { getFilterValues } from "oc/utils/get-filter-values";

export const MyLeaveHandleLeaveRequestsPage = () => {
  const { t, i18n } = useTranslation();
  const isMobile = useOcIsMobile();

  const { data, loading } = useMyLeaveOrphanedRequestsList();

  let tableId = "handleRequestsTable";

  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "name",
    pageSize: 5,
  });

  const [mutationLoading, setMutationLoading] = useState(false);
  let result = data || [];

  result = _.orderBy(
    result,
    [tableData?.sortField],
    [
      tableData?.sortDirection === "asc" || tableData?.sortDirection === "desc"
        ? tableData?.sortDirection
        : "asc",
    ],
  );

  if (tableData?.filterField) {
    let filterValues = getFilterValues(tableData?.filterField, result);
    if (!_.isEqual(filterValues, tableData?.filterValues)) {
      setTableData({
        ...tableData,
        filterValues,
      });
    }
  }

  if (tableData?.filterSelected.length > 0) {
    result = result.filter((item: any) => {
      let key = item[tableData?.filterField];
      if (key) {
        return tableData?.filterSelected.includes(key);
      } else {
        return item;
      }
    });
  }

  if (tableData?.searchField && tableData?.searchText) {
    var regex = new RegExp(tableData?.searchText, "i");
    result = result.filter((obj: any) => regex.test(obj.name));
  }

  let list = {
    rows: result,
    total: result.length,
    page: tableData.page,
    pageSize: tableData.pageSize,
  };

  const { setOpenMessage } = useOcSnackbar();

  const [approveLeaveRequest] = useMutation(MY_LEAVE_APPROVE_REQUEST, {
    onCompleted: () => {
      setMutationLoading(false);
      setOpenMessage({
        type: "success",
        message: t("MY_LEAVE_APPROVE_LEAVE_REQUEST_SUCCEEDED"),
      });
    },
    onError: (error) => {
      console.log("an error occured", { error });
      setMutationLoading(false);
      const errorMsg = error?.message || "";
      const translatedMessage = errorMsg.startsWith("MY_LEAVE_") ? t(errorMsg) : errorMsg;
      setOpenMessage({
        type: "error",
        message: translatedMessage || t("OC_SAVE_FAILED"),
      });
    },
  });

  const [rejectLeaveRequest] = useMutation(MY_LEAVE_REJECT_REQUEST, {
    onCompleted: () => {
      setMutationLoading(false);
      setOpenMessage({
        type: "success",
        message: t("MY_LEAVE_REJECT_LEAVE_REQUEST_SUCCEEDED"),
      });
    },
    onError: (error) => {
      setMutationLoading(false);
      const errorMsg = error?.message || "";
      const translatedMessage = errorMsg.startsWith("MY_LEAVE_") ? t(errorMsg) : errorMsg;
      setOpenMessage({
        type: "error",
        message: translatedMessage || t("OC_SAVE_FAILED"),
      });
    },
  });

  const [selectedRowId, setSelectedRowId] = useState<
    string | number | undefined
  >(undefined);

  interface HandleSelectRequestProps {
    id: string;
    action: string;
    [key: string]: any;
  }

  const handleSelectRequest = (props: HandleSelectRequestProps) => {
    let _id = props.id;
    let message = "";
    let locale = i18n.language.substr(0, 2).toLowerCase();
    let variables = {
      _id,
      message,
      locale,
      closedByAdmin: true,
      role: "examiner",
    };
    setMutationLoading(true);
    if (props.action === "accepted") {
      approveLeaveRequest({ variables });
    } else {
      rejectLeaveRequest({ variables });
    }
  };

  const onSelectRow = (props: any) => {
    if (isMobile) {
      setSelectedRowId(selectedRowId === props?._id ? undefined : props?._id);
    } else {
      handleSelectRequest(props);
    }
  };

  const tableDef = isMobile
    ? orphanedLeaveRequestsIsMobile
    : orphanedLeaveRequests;

  const filteredTableDef = {
    id: orphanedLeaveRequests?.id,
    columns: orphanedLeaveRequests?.columns?.filter(
      (column) =>
        !orphanedLeaveRequestsIsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  return (
    <OcPageTransition>
      <OcPageNameTitle
        title={t("MY_LEAVE_HANDLE_LEAVE_REQUESTS_PAGE_TITLE")}
        goBack={false}
      />
      <Box padding={isMobile ? "0px 0px 16px 0px" : "0px 16px 16px 16px"}>
        <OcCard
          label={t("MY_LEAVE_LEAVE_AND_ABSENCE_REQUESTS")}
          loading={loading || mutationLoading}
        >
          <OcDataTable
            data={list}
            loading={loading}
            tableDef={tableDef}
            tableData={tableData}
            setTableData={setTableData}
            pagination={true}
            onSelectRow={onSelectRow}
            localPaging={true}
            filteredTableDef={filteredTableDef}
            selectedRowId={selectedRowId}
            handleMoreButton={handleSelectRequest}
          />
        </OcCard>
      </Box>
    </OcPageTransition>
  );
};

export default MyLeaveHandleLeaveRequestsPage;
